<template>
    <v-alert v-if="visible" dense tile class="mb-0" color="indigo darken-1" dark dismissible
        @input="onDismiss">
        <v-icon small class="mr-2">mdi-eye-off-outline</v-icon>
        <span class="text-body-2">{{ bannerText }}</span>
        <v-btn small text outlined class="ml-3 normal-case" @click="goToList">
            {{ $t('blocked_quest.banner.view_now') }}
        </v-btn>
    </v-alert>
</template>

<script>
import { blockedQuestServices } from '@/services/blockedQuest.js';

const DISMISS_KEY = 'bq_banner_dismissed';

/**
 * 「有題目被素材下架卡住」的全站橫幅（F12）— 見 mangachat-studio-api
 * `docs/plans/MediaLibrary.spec.md` §2.4、ADR 0009。
 *
 * ⚠ **為什麼需要橫幅而不只是列表頁**：被停用的題目（`status = '-2'`）在關卡列表裡
 * **看不到**，學生端也拿不到——也就是說老師的課少了一題，而畫面上任何地方都不會說。
 * 他不會主動去點一個「被卡住的題目」頁面，因為他不知道有東西壞了。
 *
 * ⚠ **不能和安全警示塞在同一個 v-alert 裡**：兩者的嚴重性、顏色與去處都不同
 * （那個是學生寫了危險字眼，這個是老師的教材壞了）。堆疊由 `BannerStack` 負責。
 *
 * ⚠ 顏色刻意用 indigo：安全警示已經占了橘（一般）與紅（高風險），
 * 再用暖色會讓老師以為是同一類事情。
 *
 * ⚠ 關閉紀錄存 `sessionStorage` 並**以數字為 key**，所以數字一變（又有素材被下架、
 * 或修好了幾道）就會重新出現。刻意不做「永久關閉」——題目還壞著。
 */
export default {
    name: 'BlockedQuestBanner',
    data() {
        return {
            questCount: 0,
            homeworkCount: 0,
            dismissed: false,
        };
    },
    computed: {
        visible() {
            /// ⚠ 已經在列表頁上就不顯示：那一頁的標題把同一件事講得更清楚，
            /// 橫幅在那裡只是重複，而且「立即查看」會指向自己。
            return this.questCount > 0 && !this.dismissed
                && this.$route.name !== 'BlockedQuests';
        },
        summaryKey() {
            return `${this.questCount}:${this.homeworkCount}`;
        },
        bannerText() {
            /// ⚠ 結尾一定要有「需要處理」（2026-09-10 依需求方指示改）。
            /// 這一句的重點不是描述現況，是讓老師知道**這件事等著他做**——
            /// 只寫「素材已被下架」他會當成一則通知看過就算了。
            ///
            /// ⚠ 原本的寫法是「學生現在看不到 N 道題目」（後果在前、原因在後）。
            /// 改掉了，不要改回去：需求方要的是以「有幾道題目要處理」開頭。
            return this.$t('blocked_quest.banner.text', {
                q: this.questCount,
                h: this.homeworkCount,
            });
        },
    },
    async created() {
        await this.refresh();
        /// 列表頁重新上架、或題目編輯器換掉素材存檔後通知重抓，讓數字即時掉下來
        this.$root.$on('blocked-quest-refresh', this.refresh);
    },
    beforeDestroy() {
        this.$root.$off('blocked-quest-refresh', this.refresh);
    },
    methods: {
        async refresh() {
            try {
                const res = await blockedQuestServices.getSummary();
                this.questCount = res.blocked_quest_count || 0;
                this.homeworkCount = res.blocked_homework_count || 0;
            } catch (err) {
                /// 摘要失敗不影響頁面主功能——安靜地不顯示橫幅
                this.questCount = 0;
                return;
            }
            this.dismissed = sessionStorage.getItem(DISMISS_KEY) === this.summaryKey;
        },
        onDismiss() {
            sessionStorage.setItem(DISMISS_KEY, this.summaryKey);
            this.dismissed = true;
        },
        goToList() {
            this.$router.push({ name: 'BlockedQuests' });
        },
    },
};
</script>
